import {SupportedLocales} from '#types/locale.js';

export const contentLibraryLang = {
  [SupportedLocales.EN]: {
    contentLibrary: {
      title: 'Content Studio',
      subtitle:
        'Create, manage, and publish email and SMS templates for your activations.',
      tabs: {
        content: 'Templates',
        snippets: 'Snippets',
        creatives: 'Creatives',
        assets: 'Assets',
      },
      common: {
        errorTitle: 'Error',
        delete: 'Delete',
        deleting: 'Deleting...',
        cancel: 'Cancel',
      },
      preflight: {
        title: 'Preflight',
        status: {
          not_validated: 'Not validated',
          running: 'Running checks',
          valid: 'Passed',
          invalid: 'Critical issues found',
          incomplete: 'Checks incomplete',
          stale: 'Needs recheck',
          failed: 'Checks failed',
        },
        notValidatedDescription:
          'Run checks to review this content before using it.',
        runningDescription:
          'Running checks. Previous results may be out of date.',
        passedConfirmation: 'All required checks passed.',
        failedDescription: 'Preflight checks could not be completed.',
        runChecks: 'Run checks',
        rerun: 'Rerun checks',
        cancelChecks: 'Cancel checks',
        waitingForValidation: 'Waiting for validation',
        close: 'Close Preflight',
        locate: 'Locate',
        locateUnavailable:
          'Run checks again before locating this issue in the editor.',
        locateFailed: "We couldn't find that part of the content. Try again.",
        navigation: {
          locating: 'Taking you to the issue…',
          slow: 'This is taking longer than expected. Still locating the issue…',
          timeout:
            'Finding that spot is taking longer than expected. The editor is still working; try again when it finishes.',
          failed: "We couldn't find that part of the content. Try again.",
        },
        groups: {
          critical: 'Critical',
          error: 'Error',
          warning: 'Warning',
          warnings: 'Warnings',
          info: 'Info',
          passed: 'Passed',
        },
        unavailable: 'Unavailable checks',
        unavailableDescription:
          'Some checks could not run. Rerun Preflight to try them again.',
        unknownRule: 'Other check',
        ruleLabels: {
          email: {
            compliance: {
              unsubscribe: 'Unsubscribe link',
              physical_address: 'Physical address',
              company_name: 'Company name',
            },
            subject: {
              present: 'Subject line',
            },
            preheader: {
              present: 'Preheader',
            },
            links: {
              protocol: 'Link protocols',
            },
            variables: {
              syntax: 'Template variables',
            },
            liquid: {
              syntax: 'Liquid syntax',
            },
            markup: {
              diagnostics: 'HTML markup',
            },
            image_alt: 'Image alternative text',
            document: {
              language: 'Document language',
            },
            headings: 'Heading structure',
            source_size: 'HTML size',
          },
          sms: {
            message: {
              nonblank: 'Message text',
            },
            sender: {
              controls: 'Sender controls',
              whitespace: 'Sender whitespace',
            },
            links: {
              protocol: 'Link protocols',
            },
            variables: {
              syntax: 'Template variables',
            },
            liquid: {
              syntax: 'Liquid syntax',
            },
            segment_count: 'Message segments',
            ucs_2: 'UCS-2 encoding',
            opt_out: 'Opt-out language',
          },
          missingAltText: 'Image alternative text',
          missingCopyLink: 'Copy links',
          missingImageLink: 'Image links',
          overageImageWeight: 'Image weight',
          missingDetailsEmail: 'Email details',
          overageHtmlWeight: 'HTML weight',
          missingHeadings: 'Headings present',
          overageHeadings: 'Heading count',
          beefree: {
            template_validation: 'Template validation',
          },
          unreachableWebLink: 'Web link reachability',
          insufficientColorContrast: 'Color contrast',
          insufficientFontSize: 'Font size',
        },
        findingMessages: {
          email: {
            compliance: {
              unsubscribe:
                'Add the GrowthLoop unsubscribe destination to an unsubscribe link.',
              physical_address: 'Add the GrowthLoop physical address marker.',
              company_name: 'Add the GrowthLoop company name marker.',
            },
            subject: {
              present: 'Add a subject line.',
            },
            preheader: {
              present: 'Add preheader text.',
            },
            links: {
              protocol: {
                invalid_destination:
                  'Use a valid HTTP, HTTPS, mailto, tel, or GrowthLoop special-link destination.',
                https: 'Use HTTPS for ordinary web links.',
                duplicate_unsubscribe:
                  'Review duplicate or conflicting unsubscribe destinations.',
                noncanonical_compliance:
                  'Use the canonical GrowthLoop destination for this compliance link.',
              },
            },
            variables: {
              syntax: {
                malformed: 'Fix the malformed variable or template delimiters.',
                unsupported_marker:
                  'Use a supported GrowthLoop variable marker.',
              },
            },
            liquid: {
              syntax:
                'Fix invalid Liquid syntax or remove the unsupported filter.',
            },
            markup: {
              diagnostics:
                'Review malformed HTML markup near the indicated location.',
            },
            image_alt: 'Add descriptive alternative text for this image.',
            document: {
              language: 'Set the email document language.',
            },
            headings: 'Add a heading to give the email a clear structure.',
            source_size: 'Reduce the authored HTML source below 80 KB.',
          },
          sms: {
            message: {
              nonblank: 'Enter an SMS message.',
            },
            sender: {
              controls:
                'Remove control characters and line breaks from the sender.',
              whitespace:
                'Remove leading or trailing whitespace from the sender.',
            },
            links: {
              protocol: {
                invalid_destination:
                  'Use a valid HTTP, HTTPS, mailto, tel, or GrowthLoop special-link destination.',
                https: 'Use HTTPS for ordinary web links.',
              },
            },
            variables: {
              syntax: {
                malformed: 'Fix the malformed variable or template delimiters.',
                unsupported_marker:
                  'Use a supported GrowthLoop variable marker.',
              },
            },
            liquid: {
              syntax:
                'Fix invalid Liquid syntax or remove the unsupported filter.',
            },
            segment_count:
              'This message is estimated to use multiple SMS segments.',
            ucs_2:
              'This message requires UCS-2 encoding because it contains a non-GSM character.',
            opt_out: 'Consider including recognizable opt-out wording.',
          },
          missingAltText: 'Add alternative text to this image.',
          missingCopyLink: 'Add a link to this linked text or call to action.',
          missingImageLink: 'Add a destination link to this clickable image.',
          overageImageWeight: 'Reduce this image below 500 KB.',
          missingDetailsEmail: 'Complete the missing email details.',
          overageHtmlWeight: 'Reduce the email HTML below 80 KB.',
          missingHeadings: 'Add a heading to the email.',
          overageHeadings: 'Use exactly one H1 heading.',
          unreachableWebLink: 'Review this unreachable web link.',
          insufficientColorContrast: 'Increase the color contrast.',
          insufficientFontSize: 'Increase the font size to at least 14 px.',
          beefree: {
            template_validation: {
              generic:
                'This email needs a small update before it can be checked.',
              generic_with_warnings:
                'A few parts of this email may need updating.',
              invalid_target_version:
                'This email uses a version that cannot be opened here.',
              invalid_json:
                'Some parts of this email could not be checked. Review the content and try again.',
              invalid_table:
                'This table needs a quick fix before the email can be checked.',
              missing_template_version:
                'This email is missing version information. Try reopening it.',
              invalid_template_version:
                'This email version could not be opened. Try reopening it.',
              migration_error:
                'This email could not be updated to the current format.',
              rollback_error:
                'This email could not be restored to its previous format.',
              service_error:
                'The email could not be checked right now. Try again.',
              unknown:
                'This email could not be fully checked. Review it and try again.',
            },
          },
        },
        findingRemediations: {
          email: {
            compliance: {
              unsubscribe:
                'Use [unsubscribe] as the unsubscribe link destination.',
            },
            subject: {
              present: 'Enter subject text before saving.',
            },
            preheader: {
              present: 'Enter preheader text before saving.',
            },
          },
        },
        actions: {
          showInContent: 'Show in content',
          nextLocation: (current: number, total: number) =>
            `Next (${current} of ${total})`,
        },
        occurrences: (count: number) => `${count} locations`,
        formatPassedTitle: (title: string) => `${title} — All clear!`,
        passedTitles: {
          email: {
            compliance: {
              unsubscribe: 'Unsubscribe link included',
              physical_address: 'Physical address included',
              company_name: 'Company name included',
            },
            subject: {
              present: 'Subject line added',
            },
            preheader: {
              present: 'Preheader added',
            },
            links: {
              protocol: 'Links are ready',
            },
            variables: {
              syntax: 'Template variables are ready',
            },
            liquid: {
              syntax: 'Liquid syntax looks good',
            },
            markup: {
              diagnostics: 'HTML markup looks good',
            },
            image_alt: 'Alt text added successfully',
            document: {
              language: 'Language set correctly',
            },
            headings: 'Headings included',
            source_size: 'HTML size optimized',
          },
          sms: {
            message: {
              nonblank: 'Message text added',
            },
            sender: {
              controls: 'Sender name looks good',
              whitespace: 'Sender name looks good',
            },
            links: {
              protocol: 'Links are ready',
            },
            variables: {
              syntax: 'Template variables are ready',
            },
            liquid: {
              syntax: 'Liquid syntax looks good',
            },
            segment_count: 'Message fits in one segment',
            ucs_2: 'Message encoding looks good',
            opt_out: 'Opt-out wording included',
          },
          missingAltText: 'Alt text added successfully',
          missingCopyLink: 'All links are included',
          missingImageLink: 'All image links are included',
          overageImageWeight: 'Image size optimized',
          missingDetailsEmail: 'Email details added',
          overageHtmlWeight: 'HTML size optimized',
          missingHeadings: 'Headings included',
          overageHeadings: 'Primary heading found',
          unreachableWebLink: 'All links are reachable',
          insufficientColorContrast: 'Color choices are accessible',
          insufficientFontSize: 'Font sizes look good',
        },
        passedDescriptions: {
          email: {
            compliance: {
              unsubscribe:
                'Your email includes a clear way for people to unsubscribe.',
              physical_address:
                'Your email includes your business mailing address.',
              company_name: 'Your company name is included in the email.',
            },
            subject: {
              present: 'Your email has a subject line.',
            },
            preheader: {
              present: 'Your email has preheader text.',
            },
            links: {
              protocol: 'Your links use supported destinations.',
            },
            variables: {
              syntax:
                'Your template variables are ready to personalize this email.',
            },
            liquid: {
              syntax: 'Your Liquid tags are formatted correctly.',
            },
            markup: {
              diagnostics: 'Your email HTML is well formed.',
            },
            image_alt:
              'All images have alt text. Including it improves accessibility and readability.',
            document: {
              language: 'Screen readers will know how to read your content.',
            },
            headings:
              'Headings help organize your content and make it easier to read.',
            source_size:
              'Your email HTML is within the size guideline, so it is less likely to be clipped in most email clients.',
          },
          sms: {
            message: {
              nonblank: 'Your SMS message is ready.',
            },
            sender: {
              controls: 'Your sender name uses supported characters.',
              whitespace: 'Your sender name has no extra spaces.',
            },
            links: {
              protocol: 'Your links use supported destinations.',
            },
            variables: {
              syntax:
                'Your template variables are ready to personalize this message.',
            },
            liquid: {
              syntax: 'Your Liquid tags are formatted correctly.',
            },
            segment_count: 'Your message fits within one SMS segment.',
            ucs_2:
              'Your message uses characters supported by standard GSM encoding.',
            opt_out: 'Your message includes recognizable opt-out wording.',
          },
          missingAltText:
            'All images have alt text. Including it improves accessibility and readability.',
          missingCopyLink:
            'Every linked message or call to action has a destination.',
          missingImageLink: 'Every clickable image has a destination.',
          overageImageWeight:
            'All images are under 500 KB, keeping your design lightweight and quick to load.',
          missingDetailsEmail:
            'Your email includes the key details people need.',
          overageHtmlWeight:
            "Your email HTML is within the recommended size, so it won't be clipped in most email clients.",
          missingHeadings:
            'Your content uses headings to organize information and improve readability.',
          overageHeadings:
            'A single H1 helps screen readers find the main topic.',
          unreachableWebLink: 'Your web links are reachable.',
          insufficientColorContrast:
            'Your color choices keep text readable and accessible.',
          insufficientFontSize:
            'Your text meets size guidelines, making it easy for everyone to read.',
        },
        targetLabels: {
          unsubscribe_link: 'Unsubscribe link',
          physical_address: 'Physical address',
          company_name: 'Company name',
          subject: 'Subject',
          preheader: 'Preheader',
          link: 'Link',
          html_markup: 'HTML markup',
          image: 'Image',
          document_language: 'Document language',
          email_headings: 'Email headings',
          html_source: 'HTML source',
          message: 'Message',
          sender: 'Sender',
          non_gsm_character: 'Non-GSM character',
          email_content: 'Email content',
          alt: 'Alternative text',
          aria_label: 'Accessible label',
          href: 'Link destination',
          src: 'Source',
          title: 'Title',
          beefree_content: 'Advanced Editor content',
          html_content: 'HTML content',
        },
        targets: {
          document: 'Document',
          contentBlock: 'Content block',
          html: 'HTML content',
          subject: 'Subject',
          preheader: 'Preheader',
          sender: 'Sender',
          message: 'Message',
          line: (line: number) => `Line ${line}`,
          beefree: {
            detail_type: {
              no_heading: 'Email headings',
              no_h1_heading: 'Primary heading',
            },
          },
        },
      },
      channels: {
        email: 'Email',
        sms: 'SMS',
        push: 'Push',
        paid_media: 'Paid media',
      },
      creatives: {
        create: 'Create creative',
        edit: 'Edit creative',
        exportImage: 'Export image',
        exporting: 'Exporting…',
        defaultName: 'Untitled creative',
        nameLabel: 'Creative name',
        namePlaceholder: 'Name your creative',
        editorDescription:
          'Create or edit a paid media creative and save it to Content Studio.',
        unsupportedSource: 'Only IMG.LY creatives can be edited here',
        contentUnavailable:
          "This creative couldn't be loaded, so it has not been changed.",
        table: {
          name: 'Name',
          size: 'Size',
          status: 'Status',
          updated: 'Updated',
          searchPlaceholder: 'Search creatives',
          empty: 'No creatives yet',
        },
        deleteDialog: {
          title: 'Delete creative?',
          description: (label?: string) =>
            `"${label ?? ''}" will be permanently deleted.`,
        },
        notifications: {
          saved: 'Creative saved',
          saveFailed: 'Failed to save creative',
          exported: (filename: string) => `Exported "${filename}" to Assets`,
          exportFailed: 'Failed to export image',
          loadFailed: (message: string) =>
            `Failed to load the creative editor: ${message}`,
          deleted: 'Creative deleted',
          deleteFailed: 'Failed to delete creative',
        },
      },
      templates: {
        newEmailTemplate: 'New template',
        newEmail: 'Email template',
        newSms: 'SMS template',
        table: {
          name: 'Name',
          channel: 'Channel',
          source: 'Source',
          status: 'Status',
          updated: 'Updated',
          searchPlaceholder: 'Search templates',
          empty: 'No content templates yet',
          variantBadge: 'Variant',
          variationsCount: (count: number) =>
            `${count} ${count === 1 ? 'variation' : 'variations'}`,
          noDestinationConnections: 'No destination connections',
        },
        filtersRow: {
          destinationPlatformPlaceholder: 'Source platform',
          destinationConnectionPlaceholder: 'Source',
          destinationSearchPlaceholder: 'Search sources',
          emptyOptions: 'No matching options',
          lifecycle: {
            draft: 'Draft',
            approved: 'Approved',
            active: 'Active',
            archived: 'Archived',
          },
          aria: {
            lifecycle: 'Filter by lifecycle status',
            channel: 'Filter by channel',
            destinationPlatform: 'Filter by source platform',
            destinationConnection: 'Filter by source',
          },
        },
        actions: {
          actionsMenu: 'Template actions',
          edit: 'Edit',
          preview: 'Preview',
          createVariant: 'Create variant',
          makeEditable: 'Make editable',
          viewVariants: 'View variants',
        },
        variantsDrawer: {
          title: 'Variants',
          description: (label: string) => `Variants for "${label}"`,
          empty: 'No variants yet',
          close: 'Close variants',
        },
        previewDialog: {
          title: 'Template preview',
          close: 'Close',
          loadingPreview: 'Loading preview…',
          testDataTitle: 'Test data',
          testDataDescription:
            'Temporary values used only to render this preview.',
          invalidTestData: 'Enter a valid JSON object.',
          variation: 'Variation',
          original: 'Original',
          noVariables: 'No personalization variables detected.',
          resetTestData: 'Reset test data',
          emailPreviewControls: 'Email preview controls',
          desktop: 'Desktop',
          tablet: 'Tablet',
          mobile: 'Mobile',
          lightMode: 'Light',
          darkMode: 'Dark',
          resizeLeft: 'Resize preview from the left',
          resizeRight: 'Resize preview from the right',
          resizeBottom: 'Resize preview from the bottom',
          bindingsGlTitleVariant: 'Customize this variant',
          snippetBindingsTitle: 'Content snippets',
          snippetBindingPlaceholder: 'Select a snippet',
          saveCustomization: 'Save customization',
          savingCustomization: 'Saving…',
          moreActions: 'More actions',
          confirmUpdateTemplateTitle: 'Update template?',
          confirmUpdateTemplateDescription: (templateName: string) =>
            `Save your customization values for "${templateName}"? The template design and values from the source system will not change.`,
          confirmUpdateVariantTitle: 'Update variant?',
          confirmUpdateVariantDescription: (variantName: string) =>
            `Save your customization values for "${variantName}"? Only this variant will change; the template and other variants will not.`,
          updateTemplateSuccessToast: 'Template updated',
          updateVariantSuccessToast: 'Variant updated',
          updateBindingsErrorToast: 'Could not save bindings',
          deleteVariant: 'Delete variant',
          archive: 'Archive',
          unarchive: 'Unarchive',
          deleteVariantDialog: {
            title: 'Delete variant?',
            description: (label?: string) =>
              `"${label ?? ''}" will be permanently deleted.`,
          },
          deleteVariantSuccessToast: 'Variant deleted',
          archiveDialog: {
            titleTemplate: 'Archive template?',
            titleVariant: 'Archive variant?',
            descriptionTemplate: (label?: string) =>
              `"${label ?? ''}" will be archived and hidden from the active templates list.`,
            descriptionVariant: (label?: string) =>
              `"${label ?? ''}" will be archived and removed from this template's active variants.`,
          },
          archiveSuccessToast: 'Content archived',
          archiveFailedToast: 'Could not archive content',
          unarchiveDialog: {
            titleTemplate: 'Unarchive template?',
            titleVariant: 'Unarchive variant?',
            descriptionTemplate: (label?: string) =>
              `"${label ?? ''}" will be restored to the active templates list.`,
            descriptionVariant: (label?: string) =>
              `"${label ?? ''}" will be restored as an active variant of this template.`,
          },
          unarchiveSuccessToast: 'Content unarchived',
          unarchiveFailedToast: 'Could not unarchive content',
        },
        deleteDialog: {
          title: 'Delete template?',
          description: (label?: string) =>
            `"${label ?? ''}" will be permanently deleted.`,
        },
        makeEditableDialog: {
          title: 'Create editable copy?',
          description: (label?: string) =>
            `GrowthLoop will convert "${label ?? ''}" into a new editable copy. The original template will not be changed.`,
          nameLabel: 'New template name',
          defaultName: (label: string) => `Editable copy of ${label}`,
          confirm: 'Create editable copy',
        },
        notifications: {
          deleted: 'Content template deleted',
          deleteFailed: 'Failed to delete template',
          editableCopyCreated: 'Editable copy created',
          editableCopyFailed: 'Failed to create editable copy',
        },
        pickerDialog: {
          title: 'Pick a template',
          description: (channel: string) =>
            `Choose reusable ${channel} templates or existing campaign content.`,
          close: 'Close content picker',
          backToPicker: 'Back to templates',
          modeLabel: 'Content type',
          searchPlaceholder: 'Search templates',
          templatesTab: (count: number) => `Templates (${count})`,
          destinationContentTab: (count: number) =>
            `External Content (${count})`,
          localContentTab: (count: number) => `GrowthLoop Content (${count})`,
          platformDeliverablesTab: (count: number) => `Campaigns (${count})`,
          listTemplatesTitle: 'Templates',
          listCampaignsTitle: 'Campaigns',
          sourceFilterPlaceholder: 'Select sources',
          sourceFilterSearchPlaceholder: 'Search source destinations',
          emptySourceFilters: 'No source destinations to filter by.',
          empty: (channel: string) =>
            `No ${channel} templates found. Create one in Content Studio first.`,
          emptyTemplates: 'No templates found.',
          emptyDestinationContent: 'No external templates found.',
          emptyLocalContent: 'No uploadable templates found.',
          emptyCampaigns: 'No triggered templates found.',
          emptyDetail: 'Pick a template or campaign to preview it.',
          loadMore: 'Load more',
          previewLabel: 'Content preview',
          previewTitle: (name: string) => `${name} preview`,
          addVariation: 'Add variants',
          variantBadge: 'Variant',
          statusLabel: 'Status',
          updatedLabel: 'Updated',
          sourceLabel: 'Source',
          sourceDestinationLabel: 'Source destination',
          sourcePlatformLabel: 'Source platform',
          nativeBadge: 'Native',
          externalBadge: 'External',
          externalIdLabel: 'External ID',
          campaignLabel: 'Campaign',
          campaignIdLabel: 'Campaign ID',
          refresh: 'Refresh',
          unknownValue: 'Unknown',
          publishAndUse: 'Publish and use',
          useReusableTemplate: 'Use template',
          useRenderableTemplate: 'Use campaign',
          publishError:
            'Failed to publish template to destination. Please try again.',
        },
        publishOptionsDialog: {
          title: 'Publish settings',
          description:
            'Configure names and destination options before publishing.',
          contentNameLabel: 'Content name',
          contentNamePlaceholder: 'Name for the content in the destination',
          campaignNameLabel: 'Campaign name',
          campaignNamePlaceholder: 'Name for the new campaign',
          subjectLabel: 'Subject line',
          subjectPlaceholder: 'Subject for the email',
          sendClassificationLabel: 'Send classification',
          sendClassificationPlaceholder: 'Select a send classification',
          publicationListLabel: 'Publication list',
          publicationListPlaceholder: 'Select a publication list',
          confirm: 'Continue',
          loadingPlaceholder: 'Loading…',
          sfmcLoadFailed:
            'Could not load Marketing Cloud options. Please try again.',
        },
        thumbnailHover: {
          unavailableTitle: 'Preview unavailable',
          unavailableBody: 'This content does not have a rendered preview yet.',
        },
        cloneVariantDialog: {
          title: 'Create variants',
          loading: 'Loading template…',
          loadFailed: 'Could not load template.',
          variantCountLabel: 'Number of variants',
          variantCountHint: (min: number, max: number) =>
            `Choose a whole number from ${min} to ${max}.`,
          targetOutcomeLabel: 'Target outcome',
          targetOutcomePlaceholder: 'e.g. Increase email clicks',
          targetOutcomeHint:
            'The business result Marve should optimize the generated copy toward. It guides both generation and variant scoring.',
          extraGuidelinesLabel: 'Extra guidelines',
          extraGuidelinesPlaceholder: 'e.g. Keep all CTAs under 5 words.',
          extraGuidelinesHint:
            'Optional additional instructions for Marve — tone constraints, brand rules, or anything else to keep in mind while generating.',
          bindingsTitle: 'Variable bindings',
          bindingsTemplateTitle: 'Template variables',
          bindingsGlTitle: 'Customize this template',
          bindingsEmpty: 'No variable bindings on this template yet.',
          bindingsTemplateEmpty: 'No template variables on this content.',
          bindingsGlEmpty: 'This content has no customization fields.',
          createVariantDisabledTooltip:
            'Create variants is available only for templates with customization fields.',
          submit: 'Create variants',
          creating: 'Creating…',
          generatingTitle: 'Marve is generating variants',
          generatingMessages: [
            'Reading the template structure…',
            'Understanding customization fields…',
            'Exploring creative angles…',
            'Scoring quality and diversity…',
            'Preparing draft variants…',
          ],
          generatingSteps: [
            'Analyze template',
            'Generate candidates',
            'Review alignment',
            'Save variants',
          ],
          generationFailed: 'Generation failed',
          successToast: (count: number) =>
            count === 1 ? 'Variant created' : `${count} variants created`,
          errorToast: 'Could not create variants',
          previewTitle: 'Preview',
          previewLoading: 'Updating preview…',
          previewFailed: 'Could not load preview',
          saveCustomization: 'Save customization',
          savingCustomization: 'Saving…',
          confirmCreateTitle: 'Create variants?',
          confirmCreateDescription: (templateName: string) =>
            `New drafts will be generated from "${templateName}". The existing template will not be changed.`,
          confirmCreateContinue: 'Continue',
          variantCountStepTitle: 'Customize variant generation',
          confirmUpdateTitle: 'Update template?',
          confirmUpdateDescription: (templateName: string) =>
            `Save your customization values for "${templateName}"? The template design and values from the source system will not change.`,
          updateTemplateSuccessToast: 'Template updated',
          updateTemplateErrorToast: 'Could not update template',
        },
      },
      snippets: {
        newSnippet: 'New snippet',
        table: {
          empty: 'No snippets yet',
          searchPlaceholder: 'Search snippets',
        },
      },
      assets: {
        uploadAsset: 'Upload asset',
        uploading: 'Uploading...',
        editAsset: 'Edit asset',
        editor: {
          description: 'Edit this image and save the result as a new asset.',
          loadFailed: (message: string) =>
            `Failed to load the image editor: ${message}`,
        },
        table: {
          name: 'Name',
          type: 'Type',
          added: 'Added',
          empty: 'No assets uploaded yet',
          searchPlaceholder: 'Search assets',
        },
        actions: {
          preview: 'Preview asset',
        },
        deleteDialog: {
          title: 'Delete asset?',
          description: (label?: string) =>
            `"${label ?? ''}" will be permanently deleted. Assets used by templates cannot be deleted.`,
        },
        notifications: {
          uploaded: (filename: string) => `"${filename}" uploaded`,
          uploadFailed: (filename: string) => `Failed to upload "${filename}"`,
          saved: (filename: string) => `Saved "${filename}" to Assets`,
          saveFailed: 'Failed to save the edited image',
          deleted: 'Asset deleted',
          deleteFailed:
            'Failed to delete asset - it may be in use by a template',
        },
        uploadErrorTitle: 'Upload error',
      },
      editor: {
        start: {
          title: 'How do you want to start?',
          back: 'Back',
          template: {
            title: 'Start from a template',
            description: 'Choose from reusable email templates.',
            badge: 'Recommended',
          },
          blank: {
            title: 'Start blank',
            description:
              'A blank canvas with the footer and special links already included.',
          },
          marve: {
            title: 'Draft with Marve',
            description:
              'Open the blank canvas with AI Studio ready to draft from a brief.',
            badge: 'New',
            prompt:
              'Draft a reusable marketing email in our brand voice. Keep the footer and special links intact.',
          },
          html: {
            title: 'Advanced HTML',
            description:
              'Write or paste complete email HTML with a live rendered preview.',
          },
          templates: {
            title: 'Choose a template',
            subtitle: 'Browse available email templates.',
            searchPlaceholder: 'Search templates',
            loading: 'Loading templates',
            opening: 'Opening template...',
            empty: 'No templates found.',
            loadFailed: 'Could not load templates.',
            missingJson:
              'This template did not include editable template JSON.',
            categoryPlaceholder: 'Category',
            categoryEmptyState: 'No categories available.',
            categoryToggleAriaLabel: 'Filter templates by category',
          },
        },
        defaultName: 'Untitled email',
        defaultSnippetName: 'Untitled snippet',
        loadFailure: 'Failed to load this template. Retry to continue editing.',
        unsupportedSource:
          'Editing is only available for content created in Content Studio.',
        saved: 'Template saved',
        snippetSaved: 'Snippet saved',
        saveFailed: 'Failed to save template',
        snippetSaveFailed: 'Failed to save snippet',
        saveUnknown:
          'Could not confirm whether the template was created. Check the library before saving again to retry.',
        loadEditorFailed: 'Failed to load email editor',
        html: {
          authoringTabs: 'HTML email authoring',
          editorTab: 'Editor',
          settingsTab: 'Settings',
          settingsTitle: 'Email settings',
          settingsDescription:
            'Set the inbox metadata recipients see before opening this email.',
          theme: 'Theme',
          themes: {
            system: 'System',
            light: 'Light',
            dark: 'Dark',
          },
          subject: 'Subject',
          preheader: 'Preheader',
          codeLabel: 'Email HTML code',
          livePreview: 'Live preview',
        },
        mergeTags: {
          browseAction: 'Personalization',
          title: 'Personalization',
          close: 'Close personalization',
          description: 'Choose what you want to insert into the template.',
          typeLabel: 'Personalization type',
          types: {
            warehouseField: 'Warehouse field',
            variable: 'Variable',
            creativeVariable: 'Creative variable',
            contentSnippet: 'Content snippet',
            dynamicSnippetSlot: 'Dynamic snippet slot',
          },
          creativeVariableNameLabel: 'Creative variable name',
          creativeVariableNamePlaceholder: 'e.g. subject_line',
          creativeVariableNameHint:
            'The gl_creative_ prefix is added automatically. Existing gl_creative_ and gl_variation prefixes are preserved.',
          creativeVariableDefaultLabel: 'Default value (optional)',
          creativeVariableDefaultPlaceholder: 'e.g. Your next adventure awaits',
          creativeVariableDefaultHint:
            'Used when no value is provided at send time.',
          variableNameLabel: 'Variable name',
          variableNamePlaceholder: 'e.g. first_name',
          variableNameHint: 'Use letters, numbers, and underscores only.',
          tagPreviewLabel: 'Tag to insert',
          datasetGroupLabel: 'Dataset group',
          datasetGroupPlaceholder: 'Select a dataset group',
          datasetGroupSearchPlaceholder: 'Search dataset groups',
          datasetGroupHint:
            'The template can still be mapped to a different dataset group when it is used.',
          noDatasetGroups: 'No dataset groups found.',
          columnLabel: 'Personalization field',
          chooseDatasetGroupFirst: 'Select a dataset group first',
          columnPlaceholder: 'Select a personalization field',
          columnSearchPlaceholder: 'Search columns',
          columnHint:
            'Only the column name is inserted; no customer data is accessed.',
          noColumns: 'No matching columns found.',
          loadingColumns: 'Loading columns…',
          loadFailed: 'Could not load columns. Please try again.',
          insert: 'Insert',
          staticSnippetLabel: 'Static snippet',
          staticSnippetPlaceholder: 'Select a snippet',
          staticSnippetHint:
            'This snippet is bound when the template is saved.',
          snippetSlotLabel: 'Snippet slot name',
          snippetSlotPlaceholder: 'e.g. hero_banner',
          snippetSlotHint: 'Use letters, numbers, and underscores only.',
          snippetSlotInvalid:
            'Enter a name using only letters, numbers, and underscores.',
        },
        toolbar: {
          back: 'Back',
          templateName: 'Template name',
          templateNamePlaceholder: 'Template name',
          loading: 'Loading editor...',
          preview: 'Preview',
          exitPreview: 'Exit preview',
          save: 'Save',
          saving: 'Saving…',
          saveOptions: 'Save options',
          archive: 'Archive',
        },
        errorState: {
          title: 'Unable to load template',
          retry: 'Retry',
          backToLibrary: 'Back to library',
        },
        mcp: {
          emptyHint:
            'Describe what you want to change. AI Studio will update the email live.',
          aiEdit: 'AI Studio',
          close: 'Close',
          placeholder: 'Describe what you want to change...',
          send: 'Send',
          sessionFailed: 'Could not start AI editing session',
          sendFailed: 'Could not send AI request',
          confirmCloseWhileEditing:
            'An edit is in progress. The template may still be modified even if you close this panel. Continue?',
        },
      },
      smsEditor: {
        defaultName: 'Untitled SMS',
        sender: 'Sender',
        senderPlaceholder: 'Sender name',
        senderRequired: 'Enter a sender name.',
        senderWhitespace:
          'Remove spaces from the start and end of the sender name.',
        senderControlCharacters: 'Use a sender name without line breaks.',
        message: 'Message',
        messagePlaceholder: 'Write your SMS message…',
        characterCount: (characters: number, segments: number) =>
          `${characters} characters · ${segments} segment(s)`,
        liquidHint:
          'Liquid variables such as {{first_name}} are stored with the template and detected automatically.',
        previewPlaceholder: 'Your message preview will appear here.',
        ai: {
          title: 'AI Edit',
          placeholder: 'Describe what you want to change...',
          send: 'Send',
          close: 'Close',
          emptyHint: 'Ask me to rewrite or improve the message.',
          sessionFailed: 'Could not start AI session. Please try again.',
          sendFailed: 'AI request failed. Please try again.',
          accept: 'Accept',
          reject: 'Reject',
          statusAccepted: 'Applied', // ✓ / ✗ / ↻ / ⏱ symbol prepended at render site
          statusRejected: 'Dismissed',
          statusStale: 'Content changed — send a new message',
          statusExpired: 'Timed out',
          proposedMessage: 'Proposed message',
        },
      },
    },
  },
  [SupportedLocales.ES]: {
    contentLibrary: {
      title: 'Content Studio',
      subtitle:
        'Crea, administra y publica plantillas de email y SMS para tus activaciones.',
      tabs: {
        content: 'Plantillas',
        snippets: 'Fragmentos',
        creatives: 'Piezas creativas',
        assets: 'Recursos',
      },
      common: {
        errorTitle: 'Error',
        delete: 'Eliminar',
        deleting: 'Eliminando...',
        cancel: 'Cancelar',
      },
      preflight: {
        title: 'Preflight',
        status: {
          not_validated: 'Sin validar',
          running: 'Ejecutando comprobaciones',
          valid: 'Aprobado',
          invalid: 'Se encontraron problemas críticos',
          incomplete: 'Comprobaciones incompletas',
          stale: 'Se debe volver a comprobar',
          failed: 'Las comprobaciones fallaron',
        },
        notValidatedDescription:
          'Ejecuta las comprobaciones para revisar este contenido antes de usarlo.',
        runningDescription:
          'Ejecutando comprobaciones. Los resultados anteriores pueden estar desactualizados.',
        passedConfirmation:
          'Todas las comprobaciones obligatorias se aprobaron.',
        failedDescription:
          'No se pudieron completar las comprobaciones de Preflight.',
        runChecks: 'Ejecutar comprobaciones',
        rerun: 'Volver a ejecutar',
        cancelChecks: 'Cancelar comprobaciones',
        waitingForValidation: 'Esperando la validación',
        close: 'Cerrar Preflight',
        locate: 'Localizar',
        locateUnavailable:
          'Vuelve a ejecutar las comprobaciones antes de localizar este problema en el editor.',
        locateFailed:
          'No encontramos esa parte del contenido. Inténtalo de nuevo.',
        navigation: {
          locating: 'Vamos a mostrarte el problema…',
          slow: 'Está tardando más de lo esperado. Seguimos buscando el problema…',
          timeout:
            'Tardamos demasiado en encontrar esa parte. Inténtalo de nuevo.',
          failed: 'No encontramos esa parte del contenido. Inténtalo de nuevo.',
        },
        groups: {
          critical: 'Críticos',
          error: 'Error',
          warning: 'Advertencia',
          warnings: 'Advertencias',
          info: 'Información',
          passed: 'Aprobados',
        },
        unavailable: 'Comprobaciones no disponibles',
        unavailableDescription:
          'No se pudieron ejecutar algunas comprobaciones. Vuelve a ejecutar Preflight para intentarlo de nuevo.',
        unknownRule: 'Otra comprobación',
        ruleLabels: {
          email: {
            compliance: {
              unsubscribe: 'Enlace para cancelar la suscripción',
              physical_address: 'Dirección física',
              company_name: 'Nombre de la empresa',
            },
            subject: {
              present: 'Asunto',
            },
            preheader: {
              present: 'Texto de vista previa',
            },
            links: {
              protocol: 'Protocolos de enlace',
            },
            variables: {
              syntax: 'Variables de plantilla',
            },
            liquid: {
              syntax: 'Sintaxis de Liquid',
            },
            markup: {
              diagnostics: 'Marcado HTML',
            },
            image_alt: 'Texto alternativo de imágenes',
            document: {
              language: 'Idioma del documento',
            },
            headings: 'Estructura de encabezados',
            source_size: 'Tamaño del HTML',
          },
          sms: {
            message: {
              nonblank: 'Texto del mensaje',
            },
            sender: {
              controls: 'Controles del remitente',
              whitespace: 'Espacios del remitente',
            },
            links: {
              protocol: 'Protocolos de enlace',
            },
            variables: {
              syntax: 'Variables de plantilla',
            },
            liquid: {
              syntax: 'Sintaxis de Liquid',
            },
            segment_count: 'Segmentos del mensaje',
            ucs_2: 'Codificación UCS-2',
            opt_out: 'Texto para darse de baja',
          },
          missingAltText: 'Texto alternativo de imágenes',
          missingCopyLink: 'Enlaces en el texto',
          missingImageLink: 'Enlaces de imágenes',
          overageImageWeight: 'Peso de las imágenes',
          missingDetailsEmail: 'Detalles del correo electrónico',
          overageHtmlWeight: 'Peso del HTML',
          missingHeadings: 'Presencia de encabezados',
          overageHeadings: 'Cantidad de encabezados',
          beefree: {
            template_validation: 'Validación de plantilla',
          },
          unreachableWebLink: 'Accesibilidad de enlaces web',
          insufficientColorContrast: 'Contraste de color',
          insufficientFontSize: 'Tamaño de fuente',
        },
        findingMessages: {
          email: {
            compliance: {
              unsubscribe:
                'Añade el destino de cancelación de suscripción de GrowthLoop a un enlace para cancelar la suscripción.',
              physical_address:
                'Añade el marcador de dirección física de GrowthLoop.',
              company_name:
                'Añade el marcador de nombre de empresa de GrowthLoop.',
            },
            subject: {
              present: 'Añade un asunto.',
            },
            preheader: {
              present: 'Añade texto de vista previa.',
            },
            links: {
              protocol: {
                invalid_destination:
                  'Usa un destino HTTP, HTTPS, mailto, tel o de enlace especial de GrowthLoop válido.',
                https: 'Usa HTTPS para los enlaces web normales.',
                duplicate_unsubscribe:
                  'Revisa los destinos duplicados o contradictorios para cancelar la suscripción.',
                noncanonical_compliance:
                  'Usa el destino canónico de GrowthLoop para este enlace de cumplimiento.',
              },
            },
            variables: {
              syntax: {
                malformed:
                  'Corrige la variable o los delimitadores de plantilla con formato incorrecto.',
                unsupported_marker:
                  'Usa un marcador de variable compatible con GrowthLoop.',
              },
            },
            liquid: {
              syntax:
                'Corrige la sintaxis de Liquid o elimina el filtro no compatible.',
            },
            markup: {
              diagnostics:
                'Revisa el marcado HTML con formato incorrecto cerca de la ubicación indicada.',
            },
            image_alt: 'Añade texto alternativo descriptivo para esta imagen.',
            document: {
              language:
                'Establece el idioma del documento del correo electrónico.',
            },
            headings:
              'Añade un encabezado para dar una estructura clara al correo electrónico.',
            source_size: 'Reduce el código HTML creado a menos de 80 KB.',
          },
          sms: {
            message: {
              nonblank: 'Introduce un mensaje SMS.',
            },
            sender: {
              controls:
                'Elimina los caracteres de control y los saltos de línea del remitente.',
              whitespace:
                'Elimina los espacios iniciales o finales del remitente.',
            },
            links: {
              protocol: {
                invalid_destination:
                  'Usa un destino HTTP, HTTPS, mailto, tel o de enlace especial de GrowthLoop válido.',
                https: 'Usa HTTPS para los enlaces web normales.',
              },
            },
            variables: {
              syntax: {
                malformed:
                  'Corrige la variable o los delimitadores de plantilla con formato incorrecto.',
                unsupported_marker:
                  'Usa un marcador de variable compatible con GrowthLoop.',
              },
            },
            liquid: {
              syntax:
                'Corrige la sintaxis de Liquid o elimina el filtro no compatible.',
            },
            segment_count:
              'Se estima que este mensaje usará varios segmentos SMS.',
            ucs_2:
              'Este mensaje requiere codificación UCS-2 porque contiene un carácter que no pertenece a GSM.',
            opt_out:
              'Considera incluir un texto reconocible para darse de baja.',
          },
          missingAltText: 'Añade texto alternativo a esta imagen.',
          missingCopyLink:
            'Añade un enlace a este texto enlazado o llamada a la acción.',
          missingImageLink:
            'Añade un enlace de destino a esta imagen en la que se puede hacer clic.',
          overageImageWeight: 'Reduce esta imagen a menos de 500 KB.',
          missingDetailsEmail:
            'Completa los detalles que faltan del correo electrónico.',
          overageHtmlWeight:
            'Reduce el HTML del correo electrónico a menos de 80 KB.',
          missingHeadings: 'Añade un encabezado al correo electrónico.',
          overageHeadings: 'Usa exactamente un encabezado H1.',
          unreachableWebLink: 'Revisa este enlace web inaccesible.',
          insufficientColorContrast: 'Aumenta el contraste de color.',
          insufficientFontSize:
            'Aumenta el tamaño de la fuente a por lo menos 14 px.',
          beefree: {
            template_validation: {
              generic:
                'Este correo necesita un pequeño ajuste antes de poder revisarlo.',
              generic_with_warnings:
                'Es posible que algunas partes de este correo necesiten ajustes.',
              invalid_target_version:
                'Este correo usa una versión que no se puede abrir aquí.',
              invalid_json:
                'No se pudieron revisar algunas partes del correo. Revisa el contenido e inténtalo de nuevo.',
              invalid_table:
                'Esta tabla necesita un pequeño ajuste antes de revisar el correo.',
              missing_template_version:
                'Falta información de la versión de este correo. Intenta abrirlo de nuevo.',
              invalid_template_version:
                'No se pudo abrir esta versión del correo. Intenta abrirlo de nuevo.',
              migration_error:
                'No se pudo actualizar este correo al formato actual.',
              rollback_error:
                'No se pudo restaurar este correo a su formato anterior.',
              service_error:
                'No se pudo revisar el correo ahora. Inténtalo de nuevo.',
              unknown:
                'No se pudo revisar completamente este correo. Revísalo e inténtalo de nuevo.',
            },
          },
        },
        findingRemediations: {
          email: {
            compliance: {
              unsubscribe:
                'Usa [unsubscribe] como destino del enlace para cancelar la suscripción.',
            },
            subject: {
              present: 'Introduce el texto del asunto antes de guardar.',
            },
            preheader: {
              present: 'Introduce el texto de vista previa antes de guardar.',
            },
          },
        },
        actions: {
          showInContent: 'Mostrar en el contenido',
          nextLocation: (current: number, total: number) =>
            `Siguiente (${current} de ${total})`,
        },
        occurrences: (count: number) => `${count} ubicaciones`,
        formatPassedTitle: (title: string) => `${title} — ¡Todo listo!`,
        passedTitles: {
          email: {
            compliance: {
              unsubscribe: 'Enlace para cancelar la suscripción incluido',
              physical_address: 'Dirección física incluida',
              company_name: 'Nombre de la empresa incluido',
            },
            subject: {
              present: 'Asunto añadido',
            },
            preheader: {
              present: 'Texto de vista previa añadido',
            },
            links: {
              protocol: 'Los enlaces están listos',
            },
            variables: {
              syntax: 'Las variables de plantilla están listas',
            },
            liquid: {
              syntax: 'La sintaxis de Liquid es correcta',
            },
            markup: {
              diagnostics: 'El marcado HTML es correcto',
            },
            image_alt: 'Texto alternativo añadido correctamente',
            document: {
              language: 'Idioma configurado correctamente',
            },
            headings: 'Encabezados incluidos',
            source_size: 'Tamaño del HTML optimizado',
          },
          sms: {
            message: {
              nonblank: 'Texto del mensaje añadido',
            },
            sender: {
              controls: 'El nombre del remitente es correcto',
              whitespace: 'El nombre del remitente es correcto',
            },
            links: {
              protocol: 'Los enlaces están listos',
            },
            variables: {
              syntax: 'Las variables de plantilla están listas',
            },
            liquid: {
              syntax: 'La sintaxis de Liquid es correcta',
            },
            segment_count: 'El mensaje cabe en un segmento',
            ucs_2: 'La codificación del mensaje es correcta',
            opt_out: 'Texto para darse de baja incluido',
          },
          missingAltText: 'Texto alternativo añadido correctamente',
          missingCopyLink: 'Todos los enlaces están incluidos',
          missingImageLink: 'Todos los enlaces de imágenes están incluidos',
          overageImageWeight: 'Tamaño de imagen optimizado',
          missingDetailsEmail: 'Detalles del correo añadidos',
          overageHtmlWeight: 'Tamaño del HTML optimizado',
          missingHeadings: 'Encabezados incluidos',
          overageHeadings: 'Encabezado principal encontrado',
          unreachableWebLink: 'Todos los enlaces funcionan',
          insufficientColorContrast: 'Los colores son accesibles',
          insufficientFontSize: 'El tamaño de letra es adecuado',
        },
        passedDescriptions: {
          email: {
            compliance: {
              unsubscribe:
                'Tu correo incluye una forma clara de cancelar la suscripción.',
              physical_address:
                'Tu correo incluye la dirección postal de tu empresa.',
              company_name:
                'El nombre de tu empresa está incluido en el correo.',
            },
            subject: {
              present: 'Tu correo tiene un asunto.',
            },
            preheader: {
              present: 'Tu correo tiene texto de vista previa.',
            },
            links: {
              protocol: 'Tus enlaces usan destinos compatibles.',
            },
            variables: {
              syntax:
                'Tus variables de plantilla están listas para personalizar este correo.',
            },
            liquid: {
              syntax: 'Tus etiquetas Liquid tienen el formato correcto.',
            },
            markup: {
              diagnostics: 'El HTML de tu correo está bien formado.',
            },
            image_alt:
              'Todas las imágenes tienen texto alternativo, lo que mejora la accesibilidad y la lectura.',
            document: {
              language:
                'Los lectores de pantalla sabrán cómo leer tu contenido.',
            },
            headings:
              'Los encabezados organizan el contenido y facilitan la lectura.',
            source_size:
              'El HTML de tu correo está dentro del límite y es menos probable que se recorte en la mayoría de los clientes de correo.',
          },
          sms: {
            message: {
              nonblank: 'Tu mensaje SMS está listo.',
            },
            sender: {
              controls: 'El nombre del remitente usa caracteres compatibles.',
              whitespace:
                'El nombre del remitente no tiene espacios adicionales.',
            },
            links: {
              protocol: 'Tus enlaces usan destinos compatibles.',
            },
            variables: {
              syntax:
                'Tus variables de plantilla están listas para personalizar este mensaje.',
            },
            liquid: {
              syntax: 'Tus etiquetas Liquid tienen el formato correcto.',
            },
            segment_count: 'Tu mensaje cabe en un segmento SMS.',
            ucs_2:
              'Tu mensaje usa caracteres compatibles con la codificación GSM estándar.',
            opt_out:
              'Tu mensaje incluye un texto reconocible para darse de baja.',
          },
          missingAltText:
            'Todas las imágenes tienen texto alternativo, lo que mejora la accesibilidad y la lectura.',
          missingCopyLink:
            'Cada texto enlazado o llamada a la acción tiene un destino.',
          missingImageLink:
            'Cada imagen en la que se puede hacer clic tiene un destino.',
          overageImageWeight:
            'Todas las imágenes pesan menos de 500 KB, lo que hace que el diseño cargue más rápido.',
          missingDetailsEmail:
            'Tu correo incluye los detalles importantes que la gente necesita.',
          overageHtmlWeight:
            'El HTML de tu correo está dentro del tamaño recomendado y no se recortará en la mayoría de los clientes de correo.',
          missingHeadings:
            'Los encabezados organizan el contenido y mejoran la lectura.',
          overageHeadings:
            'Un solo H1 ayuda a los lectores de pantalla a encontrar el tema principal.',
          unreachableWebLink: 'Tus enlaces web funcionan.',
          insufficientColorContrast:
            'Tus colores mantienen el texto legible y accesible.',
          insufficientFontSize:
            'El tamaño de texto facilita la lectura para todas las personas.',
        },
        targetLabels: {
          unsubscribe_link: 'Enlace para cancelar la suscripción',
          physical_address: 'Dirección física',
          company_name: 'Nombre de la empresa',
          subject: 'Asunto',
          preheader: 'Texto de vista previa',
          link: 'Enlace',
          html_markup: 'Marcado HTML',
          image: 'Imagen',
          document_language: 'Idioma del documento',
          email_headings: 'Encabezados del correo electrónico',
          html_source: 'Código fuente HTML',
          message: 'Mensaje',
          sender: 'Remitente',
          non_gsm_character: 'Carácter que no pertenece a GSM',
          email_content: 'Contenido del correo electrónico',
          alt: 'Texto alternativo',
          aria_label: 'Etiqueta accesible',
          href: 'Destino del enlace',
          src: 'Fuente',
          title: 'Título',
          beefree_content: 'Contenido del Editor avanzado',
          html_content: 'Contenido HTML',
        },
        targets: {
          document: 'Documento',
          contentBlock: 'Bloque de contenido',
          html: 'Contenido HTML',
          subject: 'Asunto',
          preheader: 'Texto de vista previa',
          sender: 'Remitente',
          message: 'Mensaje',
          line: (line: number) => `Línea ${line}`,
          beefree: {
            detail_type: {
              no_heading: 'Encabezados del correo',
              no_h1_heading: 'Encabezado principal',
            },
          },
        },
      },
      channels: {
        email: 'Correo electrónico',
        sms: 'SMS',
        push: 'Push',
        paid_media: 'Publicidad paga',
      },
      creatives: {
        create: 'Crear pieza creativa',
        edit: 'Editar pieza creativa',
        exportImage: 'Exportar imagen',
        exporting: 'Exportando…',
        defaultName: 'Pieza creativa sin título',
        nameLabel: 'Nombre de la pieza creativa',
        namePlaceholder: 'Nombra tu pieza creativa',
        editorDescription:
          'Crea o edita una pieza creativa de publicidad paga y guárdala en Content Studio.',
        unsupportedSource:
          'Aquí solo se pueden editar piezas creativas de IMG.LY',
        contentUnavailable:
          'No se pudo cargar esta pieza creativa, por lo que no se ha modificado.',
        table: {
          name: 'Nombre',
          size: 'Tamaño',
          status: 'Estado',
          updated: 'Actualizado',
          searchPlaceholder: 'Buscar piezas creativas',
          empty: 'Todavía no hay piezas creativas',
        },
        deleteDialog: {
          title: '¿Eliminar pieza creativa?',
          description: (label?: string) =>
            `"${label ?? ''}" se eliminará permanentemente.`,
        },
        notifications: {
          saved: 'Pieza creativa guardada',
          saveFailed: 'No se pudo guardar la pieza creativa',
          exported: (filename: string) => `Se exportó "${filename}" a Recursos`,
          exportFailed: 'No se pudo exportar la imagen',
          loadFailed: (message: string) =>
            `No se pudo cargar el editor de piezas creativas: ${message}`,
          deleted: 'Pieza creativa eliminada',
          deleteFailed: 'No se pudo eliminar la pieza creativa',
        },
      },
      templates: {
        newEmailTemplate: 'Nueva plantilla',
        newEmail: 'Plantilla de correo electrónico',
        newSms: 'Plantilla de SMS',
        table: {
          name: 'Nombre',
          channel: 'Canal',
          source: 'Fuente',
          status: 'Estado',
          updated: 'Actualizado',
          searchPlaceholder: 'Buscar plantillas',
          empty: 'Todavía no hay plantillas de contenido',
          variantBadge: 'Variantes',
          variationsCount: (count: number) =>
            `${count} ${count === 1 ? 'variante' : 'variantes'}`,
          noDestinationConnections: 'Sin conexiones de destino',
        },
        filtersRow: {
          destinationPlatformPlaceholder: 'Plataforma de origen',
          destinationConnectionPlaceholder: 'Fuente',
          destinationSearchPlaceholder: 'Buscar orígenes',
          emptyOptions: 'Sin opciones coincidentes',
          lifecycle: {
            draft: 'Borrador',
            approved: 'Aprobado',
            active: 'Activo',
            archived: 'Archivado',
          },
          aria: {
            lifecycle: 'Filtrar por estado del ciclo de vida',
            channel: 'Filtrar por canal',
            destinationPlatform: 'Filtrar por plataforma de origen',
            destinationConnection: 'Filtrar por fuente',
          },
        },
        actions: {
          actionsMenu: 'Acciones de plantilla',
          edit: 'Editar',
          preview: 'Vista previa',
          createVariant: 'Crear variante',
          makeEditable: 'Hacer editable',
          viewVariants: 'Ver variantes',
        },
        variantsDrawer: {
          title: 'Variantes',
          description: (label: string) => `Variantes de "${label}"`,
          empty: 'Todavía no hay variantes',
          close: 'Cerrar variantes',
        },
        previewDialog: {
          title: 'Vista previa de la plantilla',
          close: 'Cerrar',
          loadingPreview: 'Cargando vista previa…',
          testDataTitle: 'Datos de prueba',
          testDataDescription:
            'Valores temporales usados solo para mostrar esta vista previa.',
          invalidTestData: 'Introduce un objeto JSON válido.',
          variation: 'Variación',
          original: 'Original',
          noVariables: 'No se detectaron variables de personalización.',
          resetTestData: 'Restablecer datos de prueba',
          emailPreviewControls: 'Controles de vista previa del correo',
          desktop: 'Escritorio',
          tablet: 'Tableta',
          mobile: 'Móvil',
          lightMode: 'Claro',
          darkMode: 'Oscuro',
          resizeLeft: 'Cambiar el tamaño desde la izquierda',
          resizeRight: 'Cambiar el tamaño desde la derecha',
          resizeBottom: 'Cambiar el tamaño desde abajo',
          bindingsGlTitleVariant: 'Personalizar esta variante',
          snippetBindingsTitle: 'Bloques de contenido',
          snippetBindingPlaceholder: 'Selecciona un fragmento',
          saveCustomization: 'Guardar personalización',
          savingCustomization: 'Guardando…',
          moreActions: 'Más acciones',
          confirmUpdateTemplateTitle: '¿Actualizar plantilla?',
          confirmUpdateTemplateDescription: (templateName: string) =>
            `¿Guardar los valores de personalización que ingresaste para "${templateName}"? El diseño de la plantilla y los valores del sistema de origen no cambiarán.`,
          confirmUpdateVariantTitle: '¿Actualizar variante?',
          confirmUpdateVariantDescription: (variantName: string) =>
            `¿Guardar los valores de personalización que ingresaste para "${variantName}"? Solo cambiará esta variante; la plantilla y las demás variantes no.`,
          updateTemplateSuccessToast: 'Plantilla actualizada',
          updateVariantSuccessToast: 'Variante actualizada',
          updateBindingsErrorToast: 'No se pudieron guardar los enlaces',
          deleteVariant: 'Eliminar variante',
          archive: 'Archivar',
          unarchive: 'Desarchivar',
          deleteVariantDialog: {
            title: '¿Eliminar variante?',
            description: (label?: string) =>
              `"${label ?? ''}" se eliminará permanentemente.`,
          },
          deleteVariantSuccessToast: 'Variante eliminada',
          archiveDialog: {
            titleTemplate: '¿Archivar plantilla?',
            titleVariant: '¿Archivar variante?',
            descriptionTemplate: (label?: string) =>
              `"${label ?? ''}" se archivará y se ocultará de la lista de plantillas activas.`,
            descriptionVariant: (label?: string) =>
              `"${label ?? ''}" se archivará y se eliminará de las variantes activas de esta plantilla.`,
          },
          archiveSuccessToast: 'Contenido archivado',
          archiveFailedToast: 'No se pudo archivar el contenido',
          unarchiveDialog: {
            titleTemplate: '¿Desarchivar plantilla?',
            titleVariant: '¿Desarchivar variante?',
            descriptionTemplate: (label?: string) =>
              `"${label ?? ''}" se restaurará en la lista de plantillas activas.`,
            descriptionVariant: (label?: string) =>
              `"${label ?? ''}" se restaurará como una variante activa de esta plantilla.`,
          },
          unarchiveSuccessToast: 'Contenido desarchivado',
          unarchiveFailedToast: 'No se pudo desarchivar el contenido',
        },
        deleteDialog: {
          title: '¿Eliminar plantilla?',
          description: (label?: string) =>
            `"${label ?? ''}" se eliminará permanentemente.`,
        },
        makeEditableDialog: {
          title: '¿Crear copia editable?',
          description: (label?: string) =>
            `GrowthLoop convertirá "${label ?? ''}" en una nueva copia editable. La plantilla original no cambiará.`,
          nameLabel: 'Nombre de la nueva plantilla',
          defaultName: (label: string) => `Copia editable de ${label}`,
          confirm: 'Crear copia editable',
        },
        notifications: {
          deleted: 'Plantilla de contenido eliminada',
          deleteFailed: 'No se pudo eliminar la plantilla',
          editableCopyCreated: 'Copia editable creada',
          editableCopyFailed: 'No se pudo crear una copia editable',
        },
        pickerDialog: {
          title: 'Elegir una plantilla',
          description: (channel: string) =>
            `Elige plantillas de ${channel} reutilizables o contenido de campañas existentes.`,
          close: 'Cerrar selector de contenido',
          backToPicker: 'Volver a las plantillas',
          modeLabel: 'Tipo de contenido',
          searchPlaceholder: 'Buscar plantillas',
          templatesTab: (count: number) => `Plantillas (${count})`,
          destinationContentTab: (count: number) =>
            `Contenido externo (${count})`,
          localContentTab: (count: number) =>
            `Contenido de GrowthLoop (${count})`,
          platformDeliverablesTab: (count: number) => `Campañas (${count})`,
          listTemplatesTitle: 'Plantillas',
          listCampaignsTitle: 'Campañas',
          sourceFilterPlaceholder: 'Seleccionar orígenes',
          sourceFilterSearchPlaceholder: 'Buscar destinos de origen',
          emptySourceFilters: 'No hay destinos de origen para filtrar.',
          empty: (channel: string) =>
            `No se encontraron plantillas de ${channel}. Crea una primero en Content Studio.`,
          emptyTemplates: 'No se encontraron plantillas.',
          emptyDestinationContent: 'No se encontraron plantillas externas.',
          emptyLocalContent:
            'No se encontraron plantillas disponibles para subir.',
          emptyCampaigns: 'No se encontraron plantillas activadas.',
          emptyDetail:
            'Elige una plantilla o campaña para ver una vista previa.',
          loadMore: 'Cargar más',
          previewLabel: 'Vista previa del contenido',
          previewTitle: (name: string) => `Vista previa de ${name}`,
          addVariation: 'Agregar variantes',
          variantBadge: 'Variante',
          statusLabel: 'Estado',
          updatedLabel: 'Actualizado',
          sourceLabel: 'Origen',
          sourceDestinationLabel: 'Destino de origen',
          sourcePlatformLabel: 'Plataforma de origen',
          nativeBadge: 'Nativo',
          externalBadge: 'Externo',
          externalIdLabel: 'ID externo',
          campaignLabel: 'Campaña',
          campaignIdLabel: 'ID de campaña',
          refresh: 'Actualizar',
          unknownValue: 'Desconocido',
          publishAndUse: 'Publicar y usar',
          useReusableTemplate: 'Usar plantilla',
          useRenderableTemplate: 'Usar campaña',
          publishError:
            'No se pudo publicar la plantilla en el destino. Vuelve a intentarlo.',
        },
        publishOptionsDialog: {
          title: 'Configuración de publicación',
          description:
            'Configura los nombres y las opciones del destino antes de publicar.',
          contentNameLabel: 'Nombre del contenido',
          contentNamePlaceholder: 'Nombre del contenido en el destino',
          campaignNameLabel: 'Nombre de la campaña',
          campaignNamePlaceholder: 'Nombre de la nueva campaña',
          subjectLabel: 'Asunto',
          subjectPlaceholder: 'Asunto del correo',
          sendClassificationLabel: 'Clasificación de envío',
          sendClassificationPlaceholder:
            'Selecciona una clasificación de envío',
          publicationListLabel: 'Lista de publicación',
          publicationListPlaceholder: 'Selecciona una lista de publicación',
          confirm: 'Continuar',
          loadingPlaceholder: 'Cargando…',
          sfmcLoadFailed:
            'No se pudieron cargar las opciones de Marketing Cloud. Vuelve a intentarlo.',
        },
        thumbnailHover: {
          unavailableTitle: 'Vista previa no disponible',
          unavailableBody:
            'Este contenido aún no tiene una vista previa generada.',
        },
        cloneVariantDialog: {
          title: 'Crear variantes',
          loading: 'Cargando plantilla…',
          loadFailed: 'No se pudo cargar la plantilla.',
          variantCountLabel: 'Cantidad de variantes',
          variantCountHint: (min: number, max: number) =>
            `Elige un número entero entre ${min} y ${max}.`,
          targetOutcomeLabel: 'Resultado objetivo',
          targetOutcomePlaceholder: 'p. ej., aumentar clics del correo',
          targetOutcomeHint:
            'El resultado de negocio hacia el que Marve debe optimizar el texto generado. Guía la generación y la puntuación de variantes.',
          extraGuidelinesLabel: 'Pautas adicionales',
          extraGuidelinesPlaceholder:
            'p. ej., mantén todos los CTA en menos de 5 palabras.',
          extraGuidelinesHint:
            'Instrucciones opcionales adicionales para Marve: restricciones de tono, reglas de marca o cualquier otra cosa a tener en cuenta al generar.',
          bindingsTitle: 'Valores de variables',
          bindingsTemplateTitle: 'Variables de plantilla',
          bindingsGlTitle: 'Personalizar esta plantilla',
          bindingsEmpty:
            'Aún no hay valores. Añade definiciones en la plantilla origen o guarda y vuelve a intentarlo.',
          bindingsTemplateEmpty:
            'No hay variables de plantilla en este contenido.',
          bindingsGlEmpty: 'Este contenido no tiene campos de personalización.',
          createVariantDisabledTooltip:
            'Crear variantes solo está disponible para plantillas con campos de personalización.',
          submit: 'Crear variantes',
          creating: 'Creando…',
          generatingTitle: 'Marve está generando variantes',
          generatingMessages: [
            'Leyendo la estructura de la plantilla…',
            'Entendiendo los campos de personalización…',
            'Explorando ángulos creativos…',
            'Evaluando calidad y diversidad…',
            'Preparando borradores…',
          ],
          generatingSteps: [
            'Analizar plantilla',
            'Generar candidatas',
            'Revisar alineación',
            'Guardar variantes',
          ],
          generationFailed: 'No se pudo generar',
          successToast: (count: number) =>
            count === 1 ? 'Variante creada' : `${count} variantes creadas`,
          errorToast: 'No se pudieron crear variantes',
          previewTitle: 'Vista previa',
          previewLoading: 'Actualizando vista previa…',
          previewFailed: 'No se pudo cargar la vista previa',
          saveCustomization: 'Guardar personalización',
          savingCustomization: 'Guardando…',
          confirmCreateTitle: '¿Crear variantes?',
          confirmCreateDescription: (templateName: string) =>
            `Se generarán borradores a partir de "${templateName}". La plantilla existente no cambiará.`,
          confirmCreateContinue: 'Continuar',
          variantCountStepTitle: 'Personalizar la generación de variantes',
          confirmUpdateTitle: '¿Actualizar plantilla?',
          confirmUpdateDescription: (templateName: string) =>
            `¿Guardar los valores de personalización que ingresaste para "${templateName}"? El diseño de la plantilla y los valores del sistema de origen no cambiarán.`,
          updateTemplateSuccessToast: 'Plantilla actualizada',
          updateTemplateErrorToast: 'No se pudo actualizar la plantilla',
        },
      },
      snippets: {
        newSnippet: 'Nuevo fragmento',
        table: {
          empty: 'Todavía no hay fragmentos',
          searchPlaceholder: 'Buscar fragmentos',
        },
      },
      assets: {
        uploadAsset: 'Subir recurso',
        uploading: 'Subiendo...',
        editAsset: 'Editar recurso',
        editor: {
          description:
            'Edita esta imagen y guarda el resultado como un recurso nuevo.',
          loadFailed: (message: string) =>
            `No se pudo cargar el editor de imágenes: ${message}`,
        },
        table: {
          name: 'Nombre',
          type: 'Tipo',
          added: 'Agregado',
          empty: 'Todavía no hay recursos subidos',
          searchPlaceholder: 'Buscar recursos',
        },
        actions: {
          preview: 'Vista previa del recurso',
        },
        deleteDialog: {
          title: '¿Eliminar recurso?',
          description: (label?: string) =>
            `"${label ?? ''}" se eliminará permanentemente. Los recursos usados por plantillas no se pueden eliminar.`,
        },
        notifications: {
          uploaded: (filename: string) => `Se subió "${filename}"`,
          uploadFailed: (filename: string) => `No se pudo subir "${filename}"`,
          saved: (filename: string) => `Se guardó "${filename}" en Recursos`,
          saveFailed: 'No se pudo guardar la imagen editada',
          deleted: 'Recurso eliminado',
          deleteFailed:
            'No se pudo eliminar el recurso; puede estar en uso por una plantilla',
        },
        uploadErrorTitle: 'Error de carga',
      },
      editor: {
        start: {
          title: '¿Cómo quieres empezar?',
          back: 'Volver',
          template: {
            title: 'Empezar con una plantilla',
            description: 'Elige entre plantillas de correo reutilizables.',
            badge: 'Recomendado',
          },
          blank: {
            title: 'Empezar en blanco',
            description:
              'Un lienzo en blanco con el pie de página y enlaces especiales incluidos.',
          },
          marve: {
            title: 'Crear borrador con Marve',
            description:
              'Abre el lienzo en blanco con AI Studio listo para redactar desde un brief.',
            badge: 'Nuevo',
            prompt:
              'Redacta un correo de marketing reutilizable con nuestra voz de marca. Mantén intactos el pie de página y los enlaces especiales.',
          },
          html: {
            title: 'HTML avanzado',
            description:
              'Escribe o pega el HTML completo del correo con una vista previa en vivo.',
          },
          templates: {
            title: 'Elige una plantilla',
            subtitle: 'Explora plantillas de correo disponibles.',
            searchPlaceholder: 'Buscar plantillas',
            loading: 'Cargando plantillas',
            opening: 'Abriendo plantilla...',
            empty: 'No se encontraron plantillas.',
            loadFailed: 'No se pudieron cargar las plantillas.',
            missingJson:
              'Esta plantilla no incluyó JSON editable de plantilla.',
            categoryPlaceholder: 'Categoría',
            categoryEmptyState: 'No hay categorías disponibles.',
            categoryToggleAriaLabel: 'Filtrar plantillas por categoría',
          },
        },
        defaultName: 'Correo sin título',
        defaultSnippetName: 'Fragmento sin título',
        loadFailure:
          'No se pudo cargar esta plantilla. Vuelve a intentarlo para seguir editando.',
        unsupportedSource:
          'La edición solo está disponible para contenido creado en Content Studio.',
        saved: 'Plantilla guardada',
        snippetSaved: 'Fragmento guardado',
        saveFailed: 'No se pudo guardar la plantilla',
        snippetSaveFailed: 'No se pudo guardar el fragmento',
        saveUnknown:
          'No se pudo confirmar si la plantilla se creó. Revisa la biblioteca antes de volver a guardar para reintentarlo.',
        loadEditorFailed: 'No se pudo cargar el editor de correo electrónico',
        html: {
          authoringTabs: 'Creación de correo HTML',
          editorTab: 'Editor',
          settingsTab: 'Ajustes',
          settingsTitle: 'Ajustes del correo',
          settingsDescription:
            'Configura los metadatos que los destinatarios ven antes de abrir este correo.',
          theme: 'Tema',
          themes: {
            system: 'Sistema',
            light: 'Claro',
            dark: 'Oscuro',
          },
          subject: 'Asunto',
          preheader: 'Texto de preencabezado',
          codeLabel: 'Código HTML del correo',
          livePreview: 'Vista previa en vivo',
        },
        mergeTags: {
          browseAction: 'Personalización',
          title: 'Personalización',
          close: 'Cerrar personalización',
          description: 'Elige qué quieres insertar en la plantilla.',
          typeLabel: 'Tipo de personalización',
          types: {
            warehouseField: 'Campo del almacén de datos',
            variable: 'Variable',
            creativeVariable: 'Variable creativa',
            contentSnippet: 'Fragmento de contenido',
            dynamicSnippetSlot: 'Espacio dinámico para fragmento',
          },
          creativeVariableNameLabel: 'Nombre de la variable creativa',
          creativeVariableNamePlaceholder: 'p. ej., subject_line',
          creativeVariableNameHint:
            'El prefijo gl_creative_ se agrega automáticamente. Se conservan los prefijos gl_creative_ y gl_variation existentes.',
          creativeVariableDefaultLabel: 'Valor predeterminado (opcional)',
          creativeVariableDefaultPlaceholder:
            'p. ej., Tu próxima aventura comienza aquí',
          creativeVariableDefaultHint:
            'Se usa cuando no se proporciona un valor al enviar.',
          variableNameLabel: 'Nombre de la variable',
          variableNamePlaceholder: 'p. ej., first_name',
          variableNameHint: 'Usa solo letras, números y guiones bajos.',
          tagPreviewLabel: 'Etiqueta que se insertará',
          datasetGroupLabel: 'Grupo de datos',
          datasetGroupPlaceholder: 'Selecciona un grupo de datos',
          datasetGroupSearchPlaceholder: 'Buscar grupos de datos',
          datasetGroupHint:
            'La plantilla se podrá asignar a otro grupo de datos cuando se utilice.',
          noDatasetGroups: 'No se encontraron grupos de datos.',
          columnLabel: 'Campo de personalización',
          chooseDatasetGroupFirst: 'Selecciona primero un grupo de datos',
          columnPlaceholder: 'Selecciona un campo de personalización',
          columnSearchPlaceholder: 'Buscar columnas',
          columnHint:
            'Solo se inserta el nombre de la columna; no se accede a datos de clientes.',
          noColumns: 'No se encontraron columnas coincidentes.',
          loadingColumns: 'Cargando columnas…',
          loadFailed: 'No se pudieron cargar las columnas. Inténtalo de nuevo.',
          insert: 'Insertar',
          staticSnippetLabel: 'Fragmento estático',
          staticSnippetPlaceholder: 'Selecciona un fragmento',
          staticSnippetHint:
            'Este fragmento se vincula al guardar la plantilla.',
          snippetSlotLabel: 'Nombre del espacio para fragmento',
          snippetSlotPlaceholder: 'p. ej., banner_principal',
          snippetSlotHint: 'Usa solo letras, números y guiones bajos.',
          snippetSlotInvalid:
            'Ingresa un nombre usando solo letras, números y guiones bajos.',
        },
        toolbar: {
          back: 'Volver',
          templateName: 'Nombre de la plantilla',
          templateNamePlaceholder: 'Nombre de la plantilla',
          loading: 'Cargando editor...',
          preview: 'Vista previa',
          exitPreview: 'Salir de la vista previa',
          save: 'Guardar',
          saving: 'Guardando…',
          saveOptions: 'Opciones de guardado',
          archive: 'Archivar',
        },
        errorState: {
          title: 'No se pudo cargar la plantilla',
          retry: 'Reintentar',
          backToLibrary: 'Volver a la biblioteca',
        },
        mcp: {
          emptyHint:
            'Describe lo que quieres cambiar. AI Studio actualizará el correo en tiempo real.',
          aiEdit: 'AI Studio',
          close: 'Cerrar',
          placeholder: 'Describe lo que quieres cambiar...',
          send: 'Enviar',
          sessionFailed: 'No se pudo iniciar la sesión de edición con IA',
          sendFailed: 'No se pudo enviar la solicitud a la IA',
          confirmCloseWhileEditing:
            'Hay una edición en curso. La plantilla puede seguir siendo modificada aunque cierres este panel. ¿Continuar?',
        },
      },
      smsEditor: {
        defaultName: 'SMS sin título',
        sender: 'Remitente',
        senderPlaceholder: 'Nombre del remitente',
        senderRequired: 'Escribe un nombre para el remitente.',
        senderWhitespace:
          'Quita los espacios al principio y al final del nombre del remitente.',
        senderControlCharacters:
          'Usa un nombre de remitente sin saltos de línea.',
        message: 'Mensaje',
        messagePlaceholder: 'Escribe tu mensaje SMS…',
        characterCount: (characters: number, segments: number) =>
          `${characters} caracteres · ${segments} segmento(s)`,
        liquidHint:
          'Las variables de Liquid, como {{first_name}}, se guardan con la plantilla y se detectan automáticamente.',
        previewPlaceholder: 'La vista previa del mensaje aparecerá aquí.',
        ai: {
          title: 'IA Edición',
          placeholder: 'Describe lo que quieres cambiar...',
          send: 'Enviar',
          close: 'Cerrar',
          emptyHint: 'Pídeme reescribir o mejorar el mensaje.',
          sessionFailed:
            'No se pudo iniciar la sesión de IA. Inténtalo de nuevo.',
          sendFailed: 'La solicitud de IA falló. Inténtalo de nuevo.',
          accept: 'Aceptar',
          reject: 'Rechazar',
          statusAccepted: 'Aplicado', // ✓ / ✗ / ↻ / ⏱ symbol prepended at render site
          statusRejected: 'Descartado',
          statusStale: 'El contenido cambió — envía un nuevo mensaje',
          statusExpired: 'Tiempo agotado',
          proposedMessage: 'Mensaje propuesto',
        },
      },
    },
  },
};
